import React, { useMemo } from 'react';
import { Table, InputGroup, FormControl, Pagination, Form, Button } from 'react-bootstrap';
import { useTable, useFilters, useGlobalFilter, useSortBy, useResizeColumns, useFlexLayout, usePagination } from 'react-table';
import styled from 'styled-components';
import tw from 'twin.macro';

const Container = tw.div`container bg-white rounded border border-gray-200 shadow-lg p-4`;
const Title = tw.h1`text-xl font-bold text-blue-600 mb-2 text-center`;
const TableWrapper = tw.div`mt-4 overflow-x-auto`;

const StyledTable = styled(Table)`
  ${tw`min-w-full`}
  font-size: 12px;
  font-family: 'Arial', sans-serif;
  border: 1px solid #ccc;
  border-collapse: collapse;
  width: 100%;

  thead {
    ${tw`bg-gray-200`}
    
    th {
      ${tw`border border-gray-300 text-center`}
      background-color: #f0f0f0;
      font-weight: bold;
      white-space: normal; /* Allow text wrapping */
      text-overflow: ellipsis;
      overflow: hidden;
      padding: 8px;
      position: relative;
      white-space: pre-wrap; /* Allows wrapping of long words */
      word-wrap: break-word; /* Wraps words to the next line */
    }
  }

  tbody {
    tr {
      ${tw`border-b border-gray-200`}
      
      &:hover {
        ${tw`bg-gray-100`}
      }
    }

    td {
      ${tw`border border-gray-300 py-2 px-4`}
      padding: 8px;
      white-space: pre-wrap; /* Allows wrapping of long words */
      word-wrap: break-word; /* Wraps words to the next line */
    }

    .align-right {
      text-align: right;
    }

    .align-left {
      text-align: left;
    }
  }
`;

const FilterInput = styled(FormControl)`
  ${tw`border border-gray-300`}
  height: 30px;
  font-size: 14px;
`;

const ResizableHeader = ({ className, column }) => {
  const { getHeaderProps, getSortByToggleProps, isSorted, isSortedDesc, canResize } = column;

  return (
    <th className={className} {...getHeaderProps()}>
      <div style={{ position: 'relative', width: '100%' }}>
        <span>{column.render('Header')}</span>
        {column.canSort && (
          <Button
            className="sort-button"
            {...getSortByToggleProps()}
            variant="link"
            style={{ padding: '0', marginLeft: '5px', fontSize: '12px' }} // Adjust padding and margin here
          >
            {isSorted ? (isSortedDesc ? '↓' : '↑') : '↕'}
          </Button>
        )}
        {canResize && (
          <div
            {...column.getResizerProps()}
            className="resizer"
            style={{ right: 0, position:'absolute', top: 0, bottom: 0, cursor: 'col-resize', width: '10px' }}
          />
        )}
      </div>
    </th>
  );
};

const PaginationContainer = styled.div`
  ${tw`flex justify-between items-center mt-4`}
  .pagination {
    ${tw`m-0 p-0`}
  }
  .pagination li {
    ${tw`inline-block`}
  }
`;

const PortfolioHolTable = ({ orders }) => {
  const formatAmount = (value) => {
    return parseFloat(value).toFixed(2);
  };

  const columns = useMemo(
    () => [
      { Header: 'Stock Code', accessor: 'stock_code', width: 100, align: 'left' },
      { Header: 'Exchange Code', accessor: 'exchange_code', width: 120, align: 'left' },
      { Header: 'Quantity', accessor: 'quantity', width: 80, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Average Price', accessor: 'average_price', width: 120, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Booked Profit/Loss', accessor: 'booked_profit_loss', width: 150, align: 'left', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Current Market Price', accessor: 'current_market_price', width: 150, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Change Percentage', accessor: 'change_percentage', width: 150, align: 'left', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Answer Flag', accessor: 'answer_flag', width: 120, align: 'left' },
      { Header: 'Product Type', accessor: 'product_type', width: 120, align: 'left' },
      { Header: 'Expiry Date', accessor: 'expiry_date', width: 120, align: 'right' },
      { Header: 'Strike Price', accessor: 'strike_price', width: 120, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Right', accessor: 'right', width: 80, align: 'left' },
      { Header: 'Category Index Per Stock', accessor: 'category_index_per_stock', width: 200, align: 'left' },
      { Header: 'Action', accessor: 'action', width: 100, align: 'left' },
      { Header: 'Realized Profit', accessor: 'realized_profit', width: 150, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Unrealized Profit', accessor: 'unrealized_profit', width: 150, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Open Position Value', accessor: 'open_position_value', width: 150, align: 'right', Cell: ({ value }) => formatAmount(value) },
      { Header: 'Portfolio Charges', accessor: 'portfolio_charges', width: 150, align: 'right', Cell: ({ value }) => formatAmount(value) },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    state,
    gotoPage,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions,
    setPageSize,
  } = useTable(
    {
      columns,
      data: orders || [],
      initialState: { pageIndex: 0, pageSize: 10, globalFilter: '' },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    useFlexLayout,
    usePagination
  );

  return (
    <Container>
      <Title>Order List</Title>
      <InputGroup className="my-3">
        <FilterInput
          value={state.globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Search..."
        />
      </InputGroup>
      <TableWrapper>
        <StyledTable {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <ResizableHeader
                    key={column.id}
                    column={column}
                    className="text-center"
                  />
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td 
                      key={cell.id} 
                      {...cell.getCellProps()} 
                      className={cell.column.align === 'right' ? 'align-right' : 'align-left'}
                    >
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </StyledTable>
      </TableWrapper>

      {/* Pagination Controls */}
      <PaginationContainer>
        <div>
          <span>
            Page{' '}
            <strong>
              {state.pageIndex + 1} of {pageOptions.length}
            </strong>{' '}
          </span>
        </div>
        <Pagination size="sm"> {/* Small pagination */}
          <Pagination.First onClick={() => gotoPage(0)} disabled={!canPreviousPage} />
          <Pagination.Prev onClick={() => previousPage()} disabled={!canPreviousPage} />
          {pageOptions.map((pageIndex) => (
            <Pagination.Item
              key={pageIndex}
              onClick={() => gotoPage(pageIndex)}
              active={pageIndex === state.pageIndex}
            >
              {pageIndex + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next onClick={() => nextPage()} disabled={!canNextPage} />
          <Pagination.Last onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} />
        </Pagination>
        <div>
          <span>
            Rows per page:{' '}
            <Form.Select
              size="sm" // Small select
              value={state.pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
              style={{ width: 'auto', display: 'inline-block' }} // Adjusted width
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </Form.Select>
          </span>
        </div>
      </PaginationContainer>
    </Container>
  );
};

export default PortfolioHolTable;
